/**
 * FlahaSOIL v2 — fetch-backed API client.
 *
 * Implements the `ApiV2Client` interface against the v2 Express
 * backend. The base URL is read from `VITE_API_BASE_URL` and must
 * include the `/api/v2` prefix (see `frontend/.env.example`).
 *
 * Hard rules:
 *   - All response shapes are typed via `@flaha/shared-types` — no
 *     ad-hoc DTOs are defined here.
 *   - Non-2xx responses are decoded through the standard
 *     `ApiErrorResponse` envelope and re-thrown as `ApiClientError`,
 *     which preserves the status code and the typed `code` field.
 *   - This client never falls back to the mock; switching is the job
 *     of `apiClientProvider.ts`.
 */
import {
	type AcceptInvitationRequest,
	type AcceptInvitationResponse,
	type AuthLoginResponse,
	type AuthLogoutResponse,
	type AuthMeResponse,
	type AuthRefreshResponse,
	type AuthRegisterResponse,
	type CalculateSoilTestRequest,
	type CalculateSoilTestResponse,
	type ChartSnapshotsDTO,
	type CreateInvitationRequest,
	type CreateInvitationResponse,
	type CreateProjectRequest,
	type CreateProjectResponse,
	type CreateSoilReportRequest,
	type CreateSoilReportResponse,
	type CreateSoilSampleRequest,
	type CreateSoilSampleResponse,
	type CreateSoilTestRequest,
	type CreateSoilTestResponse,
	type FlahaCalcExportResponse,
	type GenerateReportRequest,
	type GenerateReportResponse,
	type GetCurrentUserResponse,
	type GetOrganizationResponse,
	type GetProjectResponse,
	type GetReportResponse,
	type GetReportVersionResponse,
	type GetSoilInterpretationResponse,
	type GetSoilSampleResponse,
	type GetSoilTestReportResponse,
	type GetSoilTestReportSummaryResponse,
	type GetSoilTestResponse,
	type ListInvitationsResponse,
	type ListOrganizationMembersResponse,
	type ListProjectReportsResponse,
	type ListProjectsQuery,
	type ListProjectsResponse,
	type ListReportVersionsResponse,
	type LoginRequest,
	type PatchMembershipRequest,
	type PatchMembershipResponse,
	type PatchOrganizationRequest,
	type PatchOrganizationResponse,
	type PatchReportRequest,
	type PatchReportResponse,
	type RegenerateReportResponse,
	type RegisterRequest,
	type RemoveMembershipResponse,
	type RevokeInvitationResponse,
	type ScientificAnalysisResponse,
	type SwitchOrganizationRequest,
	type SwitchOrganizationResponse,
	type UserMembershipsResponse,
	isApiErrorResponse,
} from "@flaha/shared-types";
import type { ApiErrorCode } from "@flaha/shared-types";

import type { ApiV2Client } from "./apiV2Client";
import { getAccessToken } from "../auth/accessTokenStore";
import { coordinatedRefresh } from "../auth/refreshCoordinator";

const DEFAULT_BASE_URL = "http://localhost:3002/api/v2";

function getBaseUrl(): string {
	const fromEnv = import.meta.env.VITE_API_BASE_URL;
	const url =
		typeof fromEnv === "string" && fromEnv.length > 0
			? fromEnv
			: DEFAULT_BASE_URL;
	return url.replace(/\/+$/, "");
}

/**
 * Phase 9A-G: every request runs with `credentials: include` so the
 * HttpOnly refresh-token cookie is sent on `/auth/refresh` and
 * `/auth/logout`. The in-memory access token (if any) is attached as a
 * `Bearer` token; the dev-user-id header is gone — the backend no
 * longer accepts it in production and the SPA must not depend on it.
 */
function buildHeaders(extra?: Record<string, string>): HeadersInit {
	const headers: Record<string, string> = {
		Accept: "application/json",
		...(extra ?? {}),
	};
	const snap = getAccessToken();
	if (snap) headers.Authorization = `Bearer ${snap.token}`;
	return headers;
}

export class ApiClientError extends Error {
	public readonly status: number;
	public readonly code: ApiErrorCode;
	public readonly details: unknown;

	constructor(
		status: number,
		code: ApiErrorCode,
		message: string,
		details: unknown
	) {
		super(message);
		this.name = "ApiClientError";
		this.status = status;
		this.code = code;
		this.details = details;
	}
}

/**
 * Decode the standard `ApiErrorResponse` envelope from a non-2xx
 * response and throw the corresponding `ApiClientError`. Shared by the
 * JSON and Blob fetch paths so a protected binary endpoint (e.g. the
 * professional PDF) surfaces the same typed error instead of leaking a
 * raw body to the caller.
 */
async function throwResponseError(res: Response): Promise<never> {
	let body: unknown = null;
	const text = await res.text();
	if (text.length > 0) {
		try {
			body = JSON.parse(text);
		} catch {
			body = null;
		}
	}
	if (isApiErrorResponse(body)) {
		throw new ApiClientError(
			res.status,
			body.error.code,
			body.error.message,
			body.error.details
		);
	}
	throw new ApiClientError(
		res.status,
		"INTERNAL_ERROR",
		`HTTP ${res.status} ${res.statusText}`,
		body
	);
}

async function parseJsonOrThrow<T>(res: Response): Promise<T> {
	if (!res.ok) return throwResponseError(res);
	let body: unknown = null;
	const text = await res.text();
	if (text.length > 0) {
		try {
			body = JSON.parse(text);
		} catch {
			body = null;
		}
	}
	return body as T;
}

/**
 * Single source of truth for outgoing fetches against the v2 API.
 *
 * Phase 9A-G behaviour:
 *   - `credentials: "include"` so the HttpOnly refresh cookie is sent
 *     on `/auth/refresh` and `/auth/logout`.
 *   - On 401 we ask the refresh coordinator for a fresh access token.
 *     If we get one, we retry the original request ONCE with the new
 *     Bearer header. If refresh fails, we surface the original 401.
 *   - The auth endpoints opt out of the retry loop by passing
 *     `retryOn401: false` — a 401 from `/auth/refresh` IS the logout
 *     signal and must propagate.
 */
interface FetchOptions {
	method: "GET" | "POST" | "PATCH" | "DELETE";
	body?: unknown;
	retryOn401?: boolean;
}

async function apiFetch<T>(path: string, opts: FetchOptions): Promise<T> {
	const url = `${getBaseUrl()}${path}`;
	const hasBody = opts.body !== undefined;
	const init: RequestInit = {
		method: opts.method,
		credentials: "include",
		headers: buildHeaders(
			hasBody ? { "Content-Type": "application/json" } : undefined
		),
	};
	if (hasBody) init.body = JSON.stringify(opts.body);

	let res = await fetch(url, init);

	if (res.status === 401 && (opts.retryOn401 ?? true)) {
		const snap = await coordinatedRefresh();
		if (snap) {
			// Rebuild headers so the new Bearer token is picked up.
			const retryInit: RequestInit = {
				method: opts.method,
				credentials: "include",
				headers: buildHeaders(
					hasBody ? { "Content-Type": "application/json" } : undefined
				),
			};
			if (hasBody) retryInit.body = JSON.stringify(opts.body);
			res = await fetch(url, retryInit);
		}
	}

	return parseJsonOrThrow<T>(res);
}

/** Browser "Failed to fetch" / NetworkError — no HTTP status available. */
function isNetworkFetchError(err: unknown): boolean {
	if (!(err instanceof Error)) return false;
	const msg = err.message.toLowerCase();
	return (
		err.name === "TypeError" ||
		err.name === "NetworkError" ||
		msg.includes("networkerror") ||
		msg.includes("failed to fetch") ||
		msg.includes("network request failed") ||
		msg.includes("load failed")
	);
}

async function sleepMs(ms: number): Promise<void> {
	return new Promise((r) => setTimeout(r, ms));
}

/**
 * Run a fetch once, retry once on transient network failure, then map
 * network failures to a clear ApiClientError (status 0).
 */
async function fetchWithNetworkRetry(
	buildInit: () => RequestInit,
	url: string
): Promise<Response> {
	try {
		return await fetch(url, buildInit());
	} catch (err) {
		if (!isNetworkFetchError(err)) throw err;
		await sleepMs(400);
		try {
			return await fetch(url, buildInit());
		} catch (retryErr) {
			throw new ApiClientError(
				0,
				"INTERNAL_ERROR",
				"Network error while contacting the server. Check your connection and try again.",
				retryErr instanceof Error ? retryErr.message : String(retryErr)
			);
		}
	}
}

/**
 * Fetch a binary resource (e.g. the professional PDF) as a Blob using
 * the same auth as `apiFetch`: Bearer access token + `credentials:
 * include`, with a single transparent refresh-and-retry on 401. On a
 * non-2xx response the JSON error envelope is decoded and re-thrown as
 * `ApiClientError` so the caller can show a user-facing message rather
 * than opening the raw error body in a browser tab.
 */
async function fetchBlob(path: string, accept: string): Promise<Blob> {
	const url = `${getBaseUrl()}${path}`;
	const buildInit = (): RequestInit => ({
		method: "GET",
		credentials: "include",
		headers: buildHeaders({ Accept: accept }),
	});

	let res = await fetchWithNetworkRetry(buildInit, url);

	if (res.status === 401) {
		const snap = await coordinatedRefresh();
		if (snap) res = await fetchWithNetworkRetry(buildInit, url);
	}

	if (!res.ok) return throwResponseError(res);
	return res.blob();
}

/**
 * POST a JSON body and read the response as a Blob (e.g. the server PDF
 * rendered with client-supplied chart snapshots). Uses the same auth as
 * `apiFetch`/`fetchBlob`: Bearer access token + `credentials: include`,
 * with a single transparent refresh-and-retry on 401. On a non-2xx
 * response the JSON error envelope is decoded and re-thrown as
 * `ApiClientError`. The token is only ever sent in the Authorization
 * header — never in the URL or body.
 */
async function postBlob(
	path: string,
	body: unknown,
	accept: string
): Promise<Blob> {
	const url = `${getBaseUrl()}${path}`;
	const buildInit = (): RequestInit => ({
		method: "POST",
		credentials: "include",
		headers: buildHeaders({ Accept: accept, "Content-Type": "application/json" }),
		body: JSON.stringify(body),
	});

	let res = await fetchWithNetworkRetry(buildInit, url);

	if (res.status === 401) {
		const snap = await coordinatedRefresh();
		if (snap) res = await fetchWithNetworkRetry(buildInit, url);
	}

	if (!res.ok) return throwResponseError(res);
	return res.blob();
}

function getJson<T>(path: string): Promise<T> {
	return apiFetch<T>(path, { method: "GET" });
}

function postJson<T>(path: string, body: unknown): Promise<T> {
	return apiFetch<T>(path, { method: "POST", body });
}

function patchJson<T>(path: string, body: unknown): Promise<T> {
	return apiFetch<T>(path, { method: "PATCH", body });
}

function deleteJson<T>(path: string): Promise<T> {
	return apiFetch<T>(path, { method: "DELETE" });
}

/** POST helper for `/auth/*` — never retries on 401. */
function postAuthJson<T>(path: string, body: unknown): Promise<T> {
	return apiFetch<T>(path, { method: "POST", body, retryOn401: false });
}

export const realApiV2Client: ApiV2Client = {
	getMe() {
		return getJson<GetCurrentUserResponse>("/me");
	},

	// ---------------------------------------------------------------
	// Phase 9A-G — Auth
	// ---------------------------------------------------------------
	register(body: RegisterRequest) {
		return postAuthJson<AuthRegisterResponse>("/auth/register", body);
	},

	login(body: LoginRequest) {
		return postAuthJson<AuthLoginResponse>("/auth/login", body);
	},

	refresh() {
		// No body — the refresh token lives in the cookie.
		return postAuthJson<AuthRefreshResponse>("/auth/refresh", {});
	},

	logout() {
		// Uses Bearer token (logout is JWT-protected) AND clears the
		// refresh cookie server-side. retryOn401 stays the default
		// (true) so a stale access token can be refreshed once before
		// logout; if the refresh ALSO fails we surface the 401 and
		// the caller treats the session as already gone.
		return postJson<AuthLogoutResponse>("/auth/logout", {});
	},

	authMe() {
		return getJson<AuthMeResponse>("/auth/me");
	},

	verifyEmail(body) {
		return postAuthJson("/auth/verify-email", body);
	},

	resendVerification() {
		return postJson("/auth/resend-verification", {});
	},

	forgotPassword(body) {
		return postAuthJson("/auth/forgot-password", body);
	},

	resetPassword(body) {
		return postAuthJson("/auth/reset-password", body);
	},

	// Phase 9A-H — tenant listing + switching.
	listMyOrganizations() {
		return getJson<UserMembershipsResponse>("/me/organizations");
	},

	getMyUsage() {
		return getJson<import("@flaha/shared-types").GetOrgUsageResponse>(
			"/me/usage"
		);
	},

	changePassword(body: { currentPassword: string; newPassword: string }) {
		return postJson<{ ok: true }>("/auth/change-password", body);
	},

	createProfessionalCheckout(body?: {
		successUrl?: string;
		cancelUrl?: string;
	}) {
		return postJson<{ url: string; sessionId: string }>(
			"/billing/checkout/professional",
			body ?? {}
		);
	},

	createBillingPortal(body?: { returnUrl?: string }) {
		return postJson<{ url: string }>("/billing/portal", body ?? {});
	},

	leaveOrganization(organizationId: string) {
		return postJson<{ ok: true }>(
			`/organizations/${encodeURIComponent(organizationId)}/members/leave`,
			{}
		);
	},

	suspendMembership(organizationId: string, userId: string) {
		return postJson<import("@flaha/shared-types").SuspendMembershipResponse>(
			`/organizations/${encodeURIComponent(organizationId)}/members/${encodeURIComponent(userId)}/suspend`,
			{}
		);
	},

	unsuspendMembership(organizationId: string, userId: string) {
		return postJson<import("@flaha/shared-types").SuspendMembershipResponse>(
			`/organizations/${encodeURIComponent(organizationId)}/members/${encodeURIComponent(userId)}/unsuspend`,
			{}
		);
	},

	transferOwnership(
		organizationId: string,
		body: import("@flaha/shared-types").TransferOwnershipRequest
	) {
		return postJson<import("@flaha/shared-types").TransferOwnershipResponse>(
			`/organizations/${encodeURIComponent(organizationId)}/ownership/transfer`,
			body
		);
	},

	submitEnterpriseEnquiry(
		body: import("@flaha/shared-types").EnterpriseEnquiryRequest
	) {
		return postJson<import("@flaha/shared-types").EnterpriseEnquiryResponse>(
			"/enterprise/enquiry",
			body
		);
	},

	listEnterpriseEnquiries() {
		return getJson<import("@flaha/shared-types").ListEnterpriseEnquiriesResponse>(
			"/platform/enterprise-enquiries"
		);
	},

	provisionEnterprise(
		organizationId: string,
		body: import("@flaha/shared-types").ProvisionEnterpriseRequest
	) {
		return postJson<import("@flaha/shared-types").ProvisionEnterpriseResponse>(
			`/platform/organizations/${encodeURIComponent(organizationId)}/enterprise`,
			body
		);
	},

	switchOrganization(body: SwitchOrganizationRequest) {
		// Routed under `/auth/*` because it rotates the access token.
		// `postJson` (not `postAuthJson`) keeps the standard retry-on-401
		// path active so a transparently-refreshed access token can be
		// used to perform the switch.
		return postJson<SwitchOrganizationResponse>(
			"/auth/switch-organization",
			body
		);
	},

	// Phase 9B — Organization administration.
	getOrganization(organizationId: string) {
		return getJson<GetOrganizationResponse>(
			`/organizations/${encodeURIComponent(organizationId)}`
		);
	},

	patchOrganization(organizationId: string, body: PatchOrganizationRequest) {
		return patchJson<PatchOrganizationResponse>(
			`/organizations/${encodeURIComponent(organizationId)}`,
			body
		);
	},

	listOrganizationMembers(organizationId: string) {
		return getJson<ListOrganizationMembersResponse>(
			`/organizations/${encodeURIComponent(organizationId)}/members`
		);
	},

	patchMembership(
		organizationId: string,
		userId: string,
		body: PatchMembershipRequest
	) {
		return patchJson<PatchMembershipResponse>(
			`/organizations/${encodeURIComponent(organizationId)}/members/${encodeURIComponent(userId)}`,
			body
		);
	},

	removeMembership(organizationId: string, userId: string) {
		return deleteJson<RemoveMembershipResponse>(
			`/organizations/${encodeURIComponent(organizationId)}/members/${encodeURIComponent(userId)}`
		);
	},

	listInvitations(organizationId: string) {
		return getJson<ListInvitationsResponse>(
			`/organizations/${encodeURIComponent(organizationId)}/invitations`
		);
	},

	createInvitation(organizationId: string, body: CreateInvitationRequest) {
		return postJson<CreateInvitationResponse>(
			`/organizations/${encodeURIComponent(organizationId)}/invitations`,
			body
		);
	},

	revokeInvitation(organizationId: string, invitationId: string) {
		return deleteJson<RevokeInvitationResponse>(
			`/organizations/${encodeURIComponent(organizationId)}/invitations/${encodeURIComponent(invitationId)}`
		);
	},

	acceptInvitation(body: AcceptInvitationRequest) {
		return postJson<AcceptInvitationResponse>("/invitations/accept", body);
	},

	createProject(body: CreateProjectRequest) {
		return postJson<CreateProjectResponse>("/projects", body);
	},

	listProjects(query: ListProjectsQuery) {
		const params = new URLSearchParams();
		if (query.status !== undefined) params.set("status", query.status);
		const qs = params.toString();
		return getJson<ListProjectsResponse>(
			qs.length > 0 ? `/projects?${qs}` : "/projects"
		);
	},

	getProjectById(projectId: string) {
		return getJson<GetProjectResponse>(
			`/projects/${encodeURIComponent(projectId)}`
		);
	},

	createSoilSample(body: CreateSoilSampleRequest) {
		return postJson<CreateSoilSampleResponse>("/soil-samples", body);
	},

	getSoilSample(sampleId: string) {
		return getJson<GetSoilSampleResponse>(
			`/soil-samples/${encodeURIComponent(sampleId)}`
		);
	},

	createSoilTest(body: CreateSoilTestRequest) {
		return postJson<CreateSoilTestResponse>("/soil-tests", body);
	},

	getSoilTest(soilTestId: string) {
		return getJson<GetSoilTestResponse>(
			`/soil-tests/${encodeURIComponent(soilTestId)}`
		);
	},

	calculateSoilTest(soilTestId: string, body: CalculateSoilTestRequest) {
		return postJson<CalculateSoilTestResponse>(
			`/soil-tests/${encodeURIComponent(soilTestId)}/calculate`,
			body
		);
	},

	getSoilInterpretation(soilTestId: string) {
		return getJson<GetSoilInterpretationResponse>(
			`/soil-tests/${encodeURIComponent(soilTestId)}/interpretation`
		);
	},

	createSoilReport(soilTestId: string, body: CreateSoilReportRequest) {
		return postJson<CreateSoilReportResponse>(
			`/soil-tests/${encodeURIComponent(soilTestId)}/reports`,
			body
		);
	},

	getSoilTestReport(soilTestId: string) {
		return getJson<GetSoilTestReportResponse>(
			`/soil-tests/${encodeURIComponent(soilTestId)}/report`
		);
	},

	getSoilTestReportSummary(soilTestId: string) {
		return getJson<GetSoilTestReportSummaryResponse>(
			`/soil-tests/${encodeURIComponent(soilTestId)}/report?format=summary`
		);
	},

	getFlahaCalcExport(soilTestId: string) {
		return getJson<FlahaCalcExportResponse>(
			`/soil-tests/${encodeURIComponent(soilTestId)}/flahacalc-export`
		);
	},

	getScientificAnalysis(soilTestId: string) {
		return getJson<ScientificAnalysisResponse>(
			`/soil-tests/${encodeURIComponent(soilTestId)}/scientific-analysis`
		);
	},

	// Phase 8D — Report management surface.
	generateReport(soilTestId: string, body: GenerateReportRequest) {
		return postJson<GenerateReportResponse>(
			`/soil-tests/${encodeURIComponent(soilTestId)}/reports`,
			body
		);
	},

	listProjectReports(projectId: string) {
		return getJson<ListProjectReportsResponse>(
			`/projects/${encodeURIComponent(projectId)}/reports`
		);
	},

	getReport(reportId: string) {
		return getJson<GetReportResponse>(
			`/reports/${encodeURIComponent(reportId)}`
		);
	},

	listReportVersions(reportId: string) {
		return getJson<ListReportVersionsResponse>(
			`/reports/${encodeURIComponent(reportId)}/versions`
		);
	},

	getReportVersion(reportId: string, versionNumber: number) {
		return getJson<GetReportVersionResponse>(
			`/reports/${encodeURIComponent(reportId)}/versions/${versionNumber}`
		);
	},

	regenerateReport(reportId: string) {
		return postJson<RegenerateReportResponse>(
			`/reports/${encodeURIComponent(reportId)}/regenerate`,
			{}
		);
	},

	patchReport(reportId: string, body: PatchReportRequest) {
		return patchJson<PatchReportResponse>(
			`/reports/${encodeURIComponent(reportId)}`,
			body
		);
	},

	getReportVersionPreviewUrl(reportId: string, versionNumber: number) {
		return (
			`${getBaseUrl()}/reports/${encodeURIComponent(reportId)}` +
			`/versions/${versionNumber}/preview`
		);
	},

	async getReportVersionPdfBlob(
		reportId: string,
		versionNumber: number,
		chartSnapshots?: ChartSnapshotsDTO
	) {
		const path =
			`/reports/${encodeURIComponent(reportId)}` +
			`/versions/${versionNumber}/pdf`;
		// When chart snapshots are captured, POST them so the server
		// embeds the visualisations; otherwise GET the plain PDF. The
		// endpoint is protected either way and the token stays in the
		// Authorization header.
		//
		// Large snapshot POSTs can intermittently fail (browser network
		// drop, proxy body limit). Retry once without charts so the user
		// still gets a usable PDF rather than "NetworkError".
		if (chartSnapshots && Object.keys(chartSnapshots).length > 0) {
			try {
				return await postBlob(path, { chartSnapshots }, "application/pdf");
			} catch (err) {
				const network =
					err instanceof ApiClientError
						? err.status === 0
						: isNetworkFetchError(err);
				const payloadTooLarge =
					err instanceof ApiClientError &&
					(err.status === 413 ||
						err.status === 400 ||
						/body|payload|too large|limit|entity/i.test(err.message));
				if (network || payloadTooLarge) {
					console.warn(
						"PDF with chart snapshots failed; retrying without embedded charts.",
						err
					);
					return fetchBlob(path, "application/pdf");
				}
				throw err;
			}
		}
		return fetchBlob(path, "application/pdf");
	},
};
